import { useEffect, useRef, useState } from "react";
import { ClipboardList, PackageCheck, Eye, Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { WS_STATUS } from "@/components/wholesale/common";

const ICON = { sent: Send, seen: Eye, preparing: Loader2, ready: PackageCheck };

export function useOrdersBoard(enabled, remote, code, tok) {
  const [board, setBoard] = useState(null);
  const [fresh, setFresh] = useState(new Set());
  const prevReady = useRef(null);
  useEffect(() => {
    if (!enabled) { setBoard(null); return undefined; }
    let alive = true;
    const load = () => (remote ? api.get(`/public/display/${code}/board`, { params: { tok } }) : api.get("/wholesale/board")).then((r) => {
      if (!alive) return;
      const ready = new Set((r.data.orders || []).filter((o) => o.status === "ready").map((o) => o.id));
      if (prevReady.current) {
        const newly = [...ready].filter((id) => !prevReady.current.has(id));
        if (newly.length) { chime(); setFresh(new Set(newly)); setTimeout(() => alive && setFresh(new Set()), 12000); }
      }
      prevReady.current = ready;
      setBoard(r.data);
    }).catch(() => {});
    load();
    const id = setInterval(load, 8000);
    return () => { alive = false; clearInterval(id); };
  }, [enabled, remote, code, tok]);
  return [board, fresh];
}

let audioCtx = null;
function chime() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
    [[880, 0], [1175, 0.18], [1568, 0.36]].forEach(([f, at]) => {
      const o = audioCtx.createOscillator(); const g = audioCtx.createGain();
      o.type = "sine"; o.frequency.value = f; o.connect(g); g.connect(audioCtx.destination);
      const t0 = audioCtx.currentTime + at; g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(0.4, t0 + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.5);
      o.start(t0); o.stop(t0 + 0.55);
    });
  } catch { /* no audio */ }
}
// unlock audio on the first interaction (browser autoplay policy)
if (typeof window !== "undefined") window.addEventListener("pointerdown", () => { try { audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)(); audioCtx.resume(); } catch {} }, { once: true });

export function OrdersBoard({ board, fresh, t, currency }) {
  const orders = board?.orders || [];
  return (
    <section className="relative z-10 flex-1 flex flex-col p-6 gap-4" data-testid="display-orders-board" data-count={orders.length}>
      <h2 className="font-heading text-3xl flex items-center gap-3"><ClipboardList className="w-8 h-8" />{t("ws_board_title")}<span className="text-base font-sans opacity-70">{t("ws_board_sub")}</span></h2>
      {!orders.length && <div className="flex-1 grid place-items-center text-2xl opacity-70" data-testid="display-board-empty">{t("ws_board_empty")}</div>}
      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {orders.map((o) => { const I = ICON[o.status] || Send; return (
          <div key={o.id} className={`cd-glass rounded-2xl p-4 flex items-center gap-4 border-s-8 ${fresh?.has(o.id) ? "ss-ready-pulse" : ""}`} style={{ borderColor: WS_STATUS[o.status] }} data-testid={`display-board-order-${o.id}`} data-status={o.status} data-fresh={fresh?.has(o.id) ? "1" : undefined}>
            <span className="w-14 h-14 rounded-xl grid place-items-center text-white shrink-0" style={{ background: WS_STATUS[o.status] }}><I className={`w-7 h-7 ${o.status === "preparing" ? "animate-spin" : ""}`} /></span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2"><span className="font-black text-2xl" dir="ltr">#{o.number}</span><span className="font-bold text-lg truncate">{o.buyer_name}</span></div>
              <div className="text-sm opacity-80">{o.buyer_type ? t(`type_${o.buyer_type}`) : ""} · {o.items} {t("products").toLowerCase()} · {fmtNum(o.qty)} {t("qty").toLowerCase()}{o.pickup_date ? ` · ${t("ws_pickup_date")} ${o.pickup_date}` : ""}</div>
            </div>
            <div className="text-end shrink-0"><div className="font-bold text-lg" style={{ color: WS_STATUS[o.status] }}>{t(`ws_${o.status}`)}</div><div className="text-sm opacity-80" dir="ltr">{fmtNum(o.total)} {currency}</div></div>
          </div>
        ); })}
      </div>
    </section>
  );
}
